import cn from 'classnames'
import { RoundTableSection } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/roundTable/components/roundTableSection/RoundTableSection'
import { RoundTableContext } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/roundTable/context/RoundTableContext'
import { DragNDropFileUploader, IFileSettings } from 'legacy/lut/library/dragNDrop'
import { useCallback, useContext, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components/translation'

import { getFormedEltTranslation } from '../../helpers/getFormedEltTranslation'
import { TUpdateELTFormData } from '../../models/common'
import { TImagesFields, TMergedImages } from '../../models/ImageUpload'

import { Preview } from './Preview'

import styles from './CompanyImageUploader.module.scss'

interface IProps {
  onImageUpload: (fieldName: string, file: any, fileName: string) => void
  updateData: TUpdateELTFormData
  formIsFinalized: boolean
  compactHeader?: boolean
  highlightRoundTable?: boolean
  onlyLogo?: boolean
  titleVariant?: 'white' | 'dark'
  displayedImages: TMergedImages
}

export const CompanyImageUploader = ({
  onImageUpload,
  updateData,
  formIsFinalized,
  onlyLogo,
  highlightRoundTable = true,
  compactHeader = false,
  titleVariant = 'white',
  displayedImages,
}: IProps) => {
  const { ready } = useContext(RoundTableContext)
  const darkTitle = titleVariant === 'dark' || (highlightRoundTable && ready && !compactHeader)
  const handlePictureChange = useCallback(
    (fieldName: TImagesFields) => (filesInfo: IFileSettings[]) => {
      onImageUpload(fieldName, filesInfo[0].file, filesInfo[0].file.name)
      updateData(fieldName, filesInfo[0].file.name)
    },
    [onImageUpload, updateData]
  )

  const getImagePreview = useCallback(
    (fieldName: TImagesFields) => {
      return displayedImages[fieldName]
    },
    [displayedImages]
  )

  const onDelete = useCallback(
    (field: TImagesFields) => () => {
      updateData(field, '')
      onImageUpload(field, null, '')
    },
    [onImageUpload, updateData]
  )

  const logoPreview = useMemo(() => {
    const preview = getImagePreview('Logo')
    return preview.src || preview.file
  }, [getImagePreview])

  const picturePreview = useMemo(() => {
    const preview = getImagePreview('Picture')
    return preview.src || preview.file
  }, [getImagePreview])

  return (
    <RoundTableSection enabled={highlightRoundTable} showLabel floatingLabel={compactHeader}>
      <div className={cn(styles.root, { [styles.compactRoot]: compactHeader })}>
        <RenderWhen condition={!onlyLogo}>
          <div className={styles.wrapper}>
            <div className={cn(styles.title, { [styles.title__dark]: darkTitle })}>
              <Translation text={getFormedEltTranslation('picture')} />
            </div>
            {picturePreview ? (
              <Preview
                src={picturePreview}
                onDelete={onDelete('Picture')}
                formIsFinalized={formIsFinalized}
              />
            ) : (
              <DragNDropFileUploader
                disabled={formIsFinalized}
                className={styles.uploader}
                urlInputVisibility={false}
                fileListVisibility={false}
                changeFileLoader={handlePictureChange('Picture')}
                acceptedFormats={'image/*'}
                maxFileSize={0.49}
              />
            )}
          </div>
        </RenderWhen>
        <div className={styles.wrapper}>
          <div className={cn(styles.title, { [styles.title__dark]: darkTitle })}>
            <Translation text={getFormedEltTranslation('logo')} />
          </div>
          {logoPreview ? (
            <Preview
              src={logoPreview}
              onDelete={onDelete('Logo')}
              formIsFinalized={formIsFinalized}
            />
          ) : (
            <DragNDropFileUploader
              disabled={formIsFinalized}
              className={styles.uploader}
              maxFileSize={0.49}
              urlInputVisibility={false}
              fileListVisibility={false}
              changeFileLoader={handlePictureChange('Logo')}
              acceptedFormats={'image/*'}
            />
          )}
        </div>
      </div>
    </RoundTableSection>
  )
}
